import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getMonthlyTrend } from '@/infrastructure/http/metricsApi';
import { metricsKeys } from '@/infrastructure/query/metricsKeys';
import { useAuth } from '@/presentation/contexts/useAuth';
import { MetricBlock } from './MetricBlock';
import { useMetricChartTheme } from './charts/chartTheme';
import { formatCurrency, formatCurrencyCompact, formatInteger } from './metricFormatters';

interface TrendBlockProps {
  tenantId: string;
  year: number;
  month: number;
  currency?: string;
  consultorioId?: number;
}

type MetricKey = 'turnosAtendidos' | 'ingresos' | 'neto' | 'pacientesNuevos' | 'planesVendidos';

const METRICS: Array<{ key: MetricKey; labelKey: string; kind: 'int' | 'money'; color: keyof ReturnType<typeof useMetricChartTheme>['colors']; branchScoped: boolean }> = [
  { key: 'turnosAtendidos', labelKey: 'metrics.trend.metric.atendidos', kind: 'int', color: 'green', branchScoped: true },
  { key: 'ingresos', labelKey: 'metrics.trend.metric.ingresos', kind: 'money', color: 'blue', branchScoped: true },
  { key: 'neto', labelKey: 'metrics.trend.metric.neto', kind: 'money', color: 'indigo', branchScoped: true },
  { key: 'pacientesNuevos', labelKey: 'metrics.trend.metric.nuevos', kind: 'int', color: 'amber', branchScoped: false },
  { key: 'planesVendidos', labelKey: 'metrics.trend.metric.planes', kind: 'int', color: 'slate', branchScoped: false },
];

const RANGE_OPTIONS = [6, 12, 24];

function monthLabel(periodo: string, locale: string): string {
  const [y, m] = periodo.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).format(new Date(y, m - 1, 1));
}

/** Evolución mensual de la clínica: selector de métrica + rango, con línea temporal. Se auto-carga. */
export function TrendBlock({ tenantId, year, month, currency = 'ARS', consultorioId }: TrendBlockProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { token } = useAuth();
  const theme = useMetricChartTheme();
  const [months, setMonths] = useState(12);
  const [metric, setMetric] = useState<MetricKey>('ingresos');

  const availableMetrics = useMemo(
    () => METRICS.filter((m) => consultorioId === undefined || m.branchScoped),
    [consultorioId]
  );

  useEffect(() => {
    if (consultorioId !== undefined && !availableMetrics.some((m) => m.key === metric)) {
      setMetric('ingresos');
    }
  }, [consultorioId, availableMetrics, metric]);

  const trendQuery = useQuery({
    queryKey: metricsKeys.monthlyTrend(tenantId, year, month, months, consultorioId),
    queryFn: () =>
      getMonthlyTrend(token!, {
        tenantId,
        year,
        month,
        months,
        consultorioId,
      }),
    enabled: Boolean(token && tenantId && year && month),
  });

  const data = trendQuery.data ?? null;
  const isLoading = trendQuery.isLoading;
  const error =
    trendQuery.error instanceof Error ? trendQuery.error.message : null;

  const config = availableMetrics.find((option) => option.key === metric) ?? availableMetrics[0];
  const formatValue = (value: number) =>
    config.kind === 'money' ? formatCurrency(value, locale, currency) : formatInteger(value, locale);
  const formatAxis = (value: number) =>
    config.kind === 'money' ? formatCurrencyCompact(value, locale, currency) : formatInteger(value, locale);


  const chartData = useMemo(
    () =>
      (data ?? []).map((point) => ({
        label: monthLabel(point.periodo, locale),
        value: point[config.key],
      })),
    [data, config.key, locale]
  );

  const selectClass =
    'h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-950 dark:text-white';

  return (
    <MetricBlock
      title={t('metrics.trend.title')}
      description={
        consultorioId !== undefined
          ? t('metrics.trend.descriptionBranch')
          : t('metrics.trend.description')
      }
      isLoading={isLoading}
      error={error}
      isEmpty={!data || data.length === 0}
      emptyMessage={t('metrics.states.empty.message')}
    >
      {data && data.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="trend-metric">{t('metrics.trend.metricLabel')}</label>
            <select id="trend-metric" value={metric} onChange={(event) => setMetric(event.target.value as MetricKey)} className={selectClass}>
              {availableMetrics.map((option) => (
                <option key={option.key} value={option.key}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="trend-range">{t('metrics.trend.rangeLabel')}</label>
            <select id="trend-range" value={months} onChange={(event) => setMonths(Number(event.target.value))} className={selectClass}>
              {RANGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t('metrics.trend.months', { count: option })}
                </option>
              ))}
            </select>
          </div>

          <div className="h-64 min-h-64 w-full" role="img" aria-label={t('metrics.trend.ariaLabel')}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: theme.axis, fontSize: 12 }} tickLine={false} axisLine={{ stroke: theme.grid }} />
                <YAxis
                  tick={{ fill: theme.axis, fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={(value) => formatAxis(Number(value))}
                />
                <Tooltip
                  cursor={{ stroke: theme.grid }}
                  contentStyle={{ background: theme.tooltipBackground, borderColor: theme.tooltipBorder, color: theme.tooltipText }}
                  labelStyle={{ color: theme.tooltipText }}
                  itemStyle={{ color: theme.tooltipText }}
                  formatter={(value) => [formatValue(Number(value)), t(config.labelKey)]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={t(config.labelKey)}
                  stroke={theme.colors[config.color]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </MetricBlock>
  );
}
